import { cn } from '@tebuto/lib/utils'
import type { FormikValues } from 'formik'
import { useFormikContext } from 'formik'
import { LayoutGroup, motion } from 'motion/react'

interface SegmentButtonProps {
    label: string
    icon?: React.ElementType
    isSelected: boolean
    onClick: () => void
    readonly?: boolean
    labelClassNameInactive?: string
    labelClassNameActive?: string
    iconClassNameInactive?: string
    iconClassNameActive?: string
    testId?: string
}

const SegmentButton: React.FC<SegmentButtonProps> = ({
    label,
    icon: Icon,
    isSelected,
    onClick,
    readonly,
    labelClassNameInactive,
    labelClassNameActive,
    iconClassNameInactive,
    iconClassNameActive,
    testId
}) => {
    const hasCustomIconClasses = iconClassNameInactive && iconClassNameActive
    const hasCustomLabelClasses = labelClassNameInactive && labelClassNameActive

    const iconClasses = hasCustomIconClasses
        ? isSelected
            ? iconClassNameActive
            : iconClassNameInactive
        : isSelected
          ? 'text-primary-brand'
          : 'text-primary-400 group-hover:text-primary-500'

    const labelClasses = hasCustomLabelClasses
        ? isSelected
            ? labelClassNameActive
            : labelClassNameInactive
        : isSelected
          ? 'text-primary-brand'
          : 'text-primary-400 group-hover:text-primary-500'

    return (
        <div
            className={cn(
                'flex grow gap-2.5 px-5 py-3.5 justify-center items-center rounded-xl relative z-10 group select-none',
                readonly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                !isSelected && !readonly && 'active:scale-[0.98]'
            )}
            onClick={readonly ? undefined : onClick}
            data-testid={testId}
        >
            {Icon && (
                <Icon
                    className={cn(
                        'size-5 transition-all duration-200 ease-out',
                        isSelected ? 'opacity-100' : 'opacity-50 group-hover:opacity-70',
                        iconClasses
                    )}
                />
            )}
            <span
                className={cn(
                    'whitespace-nowrap font-semibold text-sm tracking-tight transition-all duration-200 ease-out',
                    labelClasses
                )}
            >
                {label}
            </span>
        </div>
    )
}

interface SegmentedButtonProps {
    name: string
    containerClassName?: string
    buttonLabelClassNameInactive?: string
    buttonIconClassNameInactive?: string
    buttonLabelClassNameActive?: string
    buttonIconClassNameActive?: string
    motionClassName?: string
    options: {
        label: string
        value: string
        icon?: React.ElementType
        testId?: string
    }[]
    readonly?: boolean
}

const SegmentedButton: React.FC<SegmentedButtonProps> = ({
    name,
    options,
    readonly,
    containerClassName,
    buttonLabelClassNameInactive,
    buttonIconClassNameInactive,
    buttonLabelClassNameActive,
    buttonIconClassNameActive,
    motionClassName
}) => {
    const { setFieldValue, values } = useFormikContext<FormikValues>()
    const selectedValue = values[name]
    const selectedIndex = options.findIndex((option) => option.value === selectedValue)

    function getGridCols(optionsLength: number) {
        switch (optionsLength) {
            case 2:
                return 'grid-cols-2'
            case 3:
                return 'grid-cols-3'
            case 4:
                return 'grid-cols-4'
            default:
                return 'grid-cols-1'
        }
    }

    const hasSelection = selectedIndex >= 0

    return (
        <LayoutGroup id={`segmented-button-${name}`}>
            <div
                className={cn(
                    'isolate relative grid w-full rounded-2xl p-1.5 border border-primary-100/80',
                    getGridCols(options.length),
                    containerClassName ? containerClassName : 'bg-linear-to-b from-primary-50/50 to-primary-100/30'
                )}
            >
                {hasSelection && (
                    <motion.div
                        layoutId={`activeButton-${name}`}
                        transition={{
                            type: 'spring',
                            stiffness: 400,
                            damping: 35
                        }}
                        className={cn(
                            'absolute top-1.5 bottom-1.5 rounded-xl pointer-events-none',
                            'bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08),0_4px_12px_rgba(0,0,0,0.05)]',
                            'border border-primary-200/60',
                            'after:absolute after:inset-0 after:rounded-xl after:ring-1 after:ring-inset after:ring-white/50',
                            motionClassName
                        )}
                        style={{
                            width: `calc(${100 / options.length}%)`,
                            left: `calc(${selectedIndex * (100 / options.length)}%)`
                        }}
                    />
                )}

                {options.map((option) => (
                    <SegmentButton
                        key={option.value}
                        labelClassNameInactive={buttonLabelClassNameInactive}
                        iconClassNameInactive={buttonIconClassNameInactive}
                        labelClassNameActive={buttonLabelClassNameActive}
                        iconClassNameActive={buttonIconClassNameActive}
                        label={option.label}
                        icon={option.icon}
                        isSelected={selectedValue === option.value}
                        onClick={() => setFieldValue(name, option.value)}
                        readonly={readonly}
                        testId={option.testId}
                    />
                ))}
            </div>
        </LayoutGroup>
    )
}

export default SegmentedButton
